/**
 * /admin/ingest/coverage — N5 — per-batch + per-source health.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, ArrowLeft, Loader2 } from "lucide-react";
import { getCoverageDashboard } from "../../api/ingest.admin.api";

export default function AdminIngestCoverage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function load() {
    setLoading(true);
    try {
      const r = await getCoverageDashboard();
      setData(r.data);
    } finally { setLoading(false); }
  }

  if (loading && !data) {
    return <div className="p-6 text-text-secondary"><Loader2 className="w-5 h-5 inline-block mr-2 animate-spin" /> Loading…</div>;
  }
  if (!data) return null;

  const summary = data.summary || {};
  const source_health = Array.isArray(data.source_health) ? data.source_health : [];
  const recent_batches = Array.isArray(data.recent_batches) ? data.recent_batches : [];

  return (
    <div className="p-6 space-y-4">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary">
        <ArrowLeft className="w-4 h-4" /> Back to dashboard
      </Link>
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Coverage + quality</h1>
          <p className="text-sm text-text-secondary mt-1">
            Per-batch and per-source health. Reflects training-corpus readiness.
          </p>
        </div>
        <button onClick={load} className="px-3 py-2 text-sm rounded-md border border-border-default hover:bg-bg-secondary flex items-center gap-1.5">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Open review items" value={summary.open_reviews} link="/admin/ingest/review" />
        <Stat label="Open anomalies" value={summary.open_anomalies} link="/admin/ingest/anomalies" />
        <Stat label="Resolved (lifetime)" value={summary.resolved_reviews} />
        <Stat label="CanonicalBalls flagged" value={summary.needs_review_balls} />
      </div>

      <section>
        <h2 className="text-base font-semibold mb-2">Per-source health</h2>
        <div className="rounded-md border border-border-default bg-bg-primary overflow-x-auto">
          <table className="w-full text-sm">
            <caption className="sr-only">Per-source ingest health</caption>
            <thead className="bg-bg-secondary text-left text-xs uppercase tracking-wider text-text-secondary">
              <tr>
                <th scope="col" className="px-4 py-2.5">Source type</th>
                <th scope="col" className="px-4 py-2.5 text-right">Files</th>
                <th scope="col" className="px-4 py-2.5 text-right">Parsed</th>
                <th scope="col" className="px-4 py-2.5 text-right">Errored</th>
                <th scope="col" className="px-4 py-2.5 text-right">Balls emitted</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-default">
              {source_health.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-text-secondary">No source files yet.</td></tr>
              )}
              {source_health.map((s) => (
                <tr key={s.source_type} className="hover:bg-bg-secondary">
                  <td className="px-4 py-2"><code className="text-xs">{s.source_type}</code></td>
                  <td className="px-4 py-2 text-right tabular-nums">{s.files}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-emerald-300">{s.parsed}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{s.errored ? <span className="text-rose-300 font-medium">{s.errored}</span> : 0}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{s.balls_emitted?.toLocaleString("en-IN")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 className="text-base font-semibold mb-2">Recent batches (last 30)</h2>
        <div className="rounded-md border border-border-default bg-bg-primary overflow-x-auto">
          <table className="w-full text-sm">
            <caption className="sr-only">Recent ingest batches</caption>
            <thead className="bg-bg-secondary text-left text-xs uppercase tracking-wider text-text-secondary">
              <tr>
                <th scope="col" className="px-4 py-2.5">Tournament</th>
                <th scope="col" className="px-4 py-2.5">State</th>
                <th scope="col" className="px-4 py-2.5">Sources</th>
                <th scope="col" className="px-4 py-2.5 text-right">Files</th>
                <th scope="col" className="px-4 py-2.5 text-right">Canonical balls</th>
                <th scope="col" className="px-4 py-2.5 text-right">Conflicts</th>
                <th scope="col" className="px-4 py-2.5 text-right">Pending review</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-default">
              {recent_batches.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-6 text-center text-text-secondary">No ingest batches yet.</td></tr>
              )}
              {recent_batches.map((b) => (
                <tr key={b.id} className="hover:bg-bg-secondary">
                  <td className="px-4 py-2">
                    <Link to={`/admin/ingest/${b.id}`} className="font-medium hover:underline">
                      {b.tournament_name} {b.season && <span className="text-text-secondary">· {b.season}</span>}
                    </Link>
                  </td>
                  <td className="px-4 py-2 text-xs">{b.state}</td>
                  <td className="px-4 py-2">
                    {(b.source_breakdown || []).map((s, i) => (
                      <span key={i} className="inline-block px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 text-[10px] mr-1 font-mono">
                        {s.source_type}×{s.n}
                      </span>
                    ))}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{b.file_count}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{b.canonical_balls_count?.toLocaleString("en-IN") || "—"}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{b.conflicts_queued || 0}</td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {b.pending_reviews ? <span className="text-amber-700 font-medium">{b.pending_reviews}</span> : 0}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value, link }) {
  const inner = (
    <div className="rounded-md border border-border-default bg-bg-primary px-4 py-3 hover:border-button-primary transition-colors">
      <div className="text-[10px] uppercase tracking-wider text-text-secondary">{label}</div>
      <div className="text-2xl font-semibold tabular-nums mt-1">{value ?? "—"}</div>
    </div>
  );
  return link ? <Link to={link}>{inner}</Link> : inner;
}
